import { zodResolver } from '@hookform/resolvers/zod';
import { CrmTaskStatus } from '@plato/data/prisma';
import { useMemo, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { plainTextToLexicalSync } from '../../../lib/rich-text/plain-text-to-lexical.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { crmTaskInputDtoSchema } from '../../../server/dto/crm-task-input-dto.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import { TaskAttachmentsField } from './task-attachments-field.tsx';
import { TaskDescriptionField } from './task-description-field.tsx';
import { TaskFormFields } from './task-form-fields.tsx';

type TaskFormDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: CrmTaskInputDTO) => void | Promise<void>;
    title: string;
    links?: Pick<CrmTaskDTO['links'][0], 'modelId' | 'modelType' | 'title'>[];
    description?: string;
    assignedToId?: string;
    hideRelatedDetailsLink?: boolean;
};

export const TaskFormDialog = ({
    open,
    onClose,
    onSubmit,
    title,
    links,
    description,
    assignedToId,
    hideRelatedDetailsLink,
}: TaskFormDialogProps): ReactNode => {
    const appState = useAppState();
    const { currentSessionId } = useVoiceRecording();

    const defaultValues = useMemo(() => {
        return {
            dueDate: new Date(),
            links: links || [],
            assignedToId: assignedToId ?? appState.platoUser.id,
            assignedById: appState.platoUser.id || '',
            createdById: appState.platoUser.id || '',
            status: CrmTaskStatus.OPEN,
            description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
            attachments: [],
        };
    }, [appState.platoUser.id, assignedToId, links, description]);

    const form = useForm<CrmTaskInputDTO>({
        defaultValues,
        resolver: zodResolver(crmTaskInputDtoSchema),
    });

    const {
        handleSubmit,
        formState: { isValid: isFormValid, isDirty, isSubmitting },
    } = form;

    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(() => {
            onClose();
        });
    };

    const submit = handleSubmit(async (values) => {
        try {
            await onSubmit(values);
            onClose();
        } catch {
            // caller surfaces the error (e.g. toast); keep the dialog open
        }
    });

    return (
        <Dialog showClose open={open} onClose={handleClose}>
            <DialogForm>
                <DialogForm.Header title={title} />
                <DialogForm.Content>
                    <DialogForm.ItemList>
                        <TaskDescriptionField form={form} />
                    </DialogForm.ItemList>
                    <DialogForm.Group>
                        <TaskFormFields form={form} hideRelatedDetailsLink={hideRelatedDetailsLink} />
                        <TaskAttachmentsField form={form} />
                    </DialogForm.Group>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    isLoading={isSubmitting}
                    onCancel={handleClose}
                    onSubmit={submit}
                    disabled={!isFormValid || !!currentSessionId}
                />
            </DialogForm>
        </Dialog>
    );
};
